/**
 * Iznos u evrima, sa razlaganjem po valutama na prelazak mišem.
 *
 * Zbir preko valuta bez preračuna nema značenje: u istoriji je 92.798 ponuda u dinarima i
 * 15.904 u evrima, pa bi prost zbir izgledao kao evri a bio uglavnom dinari. Zato je vidljivi
 * broj uvek preračunat, a iznosi po valutama stoje iza dugmeta, jer se često i traže.
 *
 * Preračun radi server, po kursu na dan dokumenta: posao iz 2008. vredeo je onoliko koliko je
 * tada vredeo, a današnji kurs bi ga izobličio.
 *
 * Oblačić se postavlja prema prozoru, ne prema kartici u kojoj stoji. Kanban se pomera
 * vodoravno i kolone imaju svoje okvire, pa bi oblačić unutar kolone bio odsečen ili bi
 * prekrio kartice ispod sebe.
 */
import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import Ic from "./icons.jsx";
import { formatMoneyCode } from "../lib/format";

/** Valuta se ispisuje šifrom (RSD, EUR, USD), ne znakom: „US$" pored „RSD" deluje kao greška. */
const novac = (n: number, valuta: string, jezik: string) =>
  formatMoneyCode(n, valuta, jezik, { maximumFractionDigits: 0 });

export function AmountByCurrency({
  eur,
  byCurrency,
  locale,
  currency = "EUR",
}: {
  /** Zbir preračunat u evre — ili sirov iznos, kad je `currency` izabrana valuta. */
  eur: number | null | undefined;
  /** Zbir po valuti, onako kako stoji na dokumentima. */
  byCurrency?: Record<string, number>;
  locale: string;
  /**
   * Valuta u kojoj je vidljivi broj. Podrazumevano evro, jer se zbir preko valuta inače ne može
   * sabrati. Kad je ekran sveden na jednu valutu, broj je tačan i preračuna nema, pa nema ni
   * šta da se razlaže: dugme sa razlaganjem tada ne izlazi.
   */
  currency?: string;
}) {
  const { t } = useTranslation();
  const [otvoreno, setOtvoreno] = useState(false);
  const [mesto, setMesto] = useState<{ top: number; left: number } | null>(null);
  const dugmeRef = useRef<HTMLButtonElement>(null);

  const jednaValuta = currency !== "EUR";
  const stavke = jednaValuta
    ? []
    : Object.entries(byCurrency ?? {})
        .filter(([, n]) => n > 0)
        .sort((a, b) => b[1] - a[1]);

  useLayoutEffect(() => {
    if (!otvoreno || !dugmeRef.current) return;
    const r = dugmeRef.current.getBoundingClientRect();
    const sirina = 230;
    const visina = 40 + stavke.length * 22 + 52;
    // Ispod dugmeta kad ima mesta, iznad kad nema; uvek unutar prozora po širini.
    const ispod = r.bottom + 6 + visina < window.innerHeight;
    setMesto({
      top: ispod ? r.bottom + 6 : Math.max(8, r.top - visina - 6),
      left: Math.min(Math.max(8, r.right - sirina), window.innerWidth - sirina - 8),
    });
  }, [otvoreno, stavke.length]);

  // NULA NIJE CRTA. `!eur` je istina i za 0, pa je red sa saldom nula do 13.10.2026 prikazivao
  // crtu — ali samo kad razlaganja nema. Dok je valuta bila podrazumevani evro, razlaganje je
  // gotovo uvek postojalo i to je kvar krilo; kad ekran prosledi izabranu valutu, razlaganja nema
  // nikad, pa bi SVAKI red sa nulom postao crta. Crta znači „nema podatka", nula znači nula.
  if (eur == null && stavke.length === 0) return <span style={{ color: "var(--ink-4)" }}>—</span>;

  return (
    <span style={omot}>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{novac(eur ?? 0, currency, locale)}</span>
      {stavke.length > 0 && (
        <>
          <button
            ref={dugmeRef}
            type="button"
            className="btn ghost sm"
            style={dugme}
            aria-expanded={otvoreno}
            aria-label={t("amount.byCurrency.aria", "Prikaži iznose po valutama")}
            onMouseEnter={() => setOtvoreno(true)}
            onMouseLeave={() => setOtvoreno(false)}
            onFocus={() => setOtvoreno(true)}
            onBlur={() => setOtvoreno(false)}
            onClick={() => setOtvoreno((x) => !x)}
          >
            <Ic.info width={15} height={15} />
          </button>
          {otvoreno && mesto && (
            <span role="tooltip" style={{ ...oblacic, top: mesto.top, left: mesto.left }}>
              <span style={naslov}>{t("amount.byCurrency.title", "Po valutama")}</span>
              {stavke.map(([valuta, n]) => (
                <span key={valuta} style={red}>
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>{novac(n, valuta, locale)}</span>
                </span>
              ))}
              <span style={napomena}>
                {t("amount.byCurrency.note", "Zbir u evrima je preračunat po kursu na dan dokumenta.")}
              </span>
            </span>
          )}
        </>
      )}
    </span>
  );
}

const omot: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6 };
/**
 * Dugme je kvadrat koji drži ikonu u sredini.
 *
 * `.btn` centrira samo uspravno (`align-items:center`), pa je znak bez `justify-content` stajao
 * uz levu ivicu. Uspravno ga je pomerala i osnovna linija teksta: slovo „i" nema donji produžetak,
 * pa je njegovo mastilo sedelo u gornjem delu reda i delovalo podignuto. Ikona nema osnovnu
 * liniju, pa oba problema nestaju.
 */
const dugme: CSSProperties = {
  width: 18, height: 18, minWidth: 18, padding: 0, borderRadius: "50%",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  color: "var(--ink-4)", flex: "none",
};
const oblacic: CSSProperties = {
  position: "fixed", zIndex: 60, width: 230,
  display: "flex", flexDirection: "column", gap: 4,
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md, 12px)", boxShadow: "var(--shadow-2, var(--shadow-1))",
  padding: "10px 12px", pointerEvents: "none",
};
const naslov: CSSProperties = {
  fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const red: CSSProperties = {
  display: "flex", justifyContent: "flex-end", fontSize: 13, whiteSpace: "nowrap",
};
const napomena: CSSProperties = {
  fontSize: 11, color: "var(--ink-3)", lineHeight: 1.35,
  borderTop: "1px solid var(--border)", paddingTop: 6, marginTop: 2,
};
